import "./LessonAreaComponent.css";

import {
    ConversationConfig,
    OpenAiCompletionRequest,
} from "../api/openAi/OpenAiCompletionRequest";
import {
    LessonDay,
    LessonDayQuestion,
} from "../api/bsf/response/AllLessonsResponse";
import {
    PinnedScripturesProvider,
    usePinnedScriptures,
} from "../context/PinnedScripturesContext";
import React, { useContext, useEffect, useRef, useState } from "react";

import AllScripturesResponse from "../api/bsf/response/AllScripturesResponse";
import AnswersResponse from "../api/bsf/response/AnswersResponse";
import { AuthContextHolder } from "../api/bsf/AuthContext";
import LectureAndNotesComponent from "./LectureAndNotesComponent";
import PinnedScripturesPanel from "./PinnedScripturesPanel";
import { SaveQuestionRequest } from "../api/bsf/requests/SaveQuestionRequest";
import Scripture from "./ScriptureComponent";
import SettingsContext from "../context/SettingsContext";
import StringUtils from "../utils/StringUtils";
import { TypeaheadTextarea } from "./TypeaheadTextarea";
import debounce from "lodash.debounce";

interface LessonDayProps {
    lessonDay: LessonDay | undefined;
    previousLessonId: number | undefined;
    answersData: AnswersResponse | undefined;
    scripturesData: AllScripturesResponse | undefined;
    onAnswerChange: (newAnswerData: AnswersResponse) => void;
}

type SaveStatus = "idle" | "unsaved" | "saving" | "saved";

interface SaveIndicatorProps {
    status: SaveStatus;
}

const SaveIndicator: React.FC<SaveIndicatorProps> = ({ status }) => {
    if (status === "idle") return null;

    return (
        <div className={`save-indicator save-indicator-${status}`}>
            {status === "unsaved" && (
                <>
                    <span className="save-indicator-icon">●</span>
                    <span className="save-indicator-text">Unsaved changes</span>
                </>
            )}
            {status === "saving" && (
                <>
                    <span className="save-indicator-icon saving">◐</span>
                    <span className="save-indicator-text">Saving...</span>
                </>
            )}
            {status === "saved" && (
                <>
                    <span className="save-indicator-icon">✓</span>
                    <span className="save-indicator-text">Saved</span>
                </>
            )}
        </div>
    );
};

const LessonAreaContent: React.FC<LessonDayProps> = ({
    lessonDay,
    previousLessonId,
    answersData,
    scripturesData,
    onAnswerChange,
}) => {
    const settings = useContext(SettingsContext);
    const { pinnedScriptures, panelWidth } = usePinnedScriptures();

    const completionPhrase = "The sentence I want completed starts with this:";

    // Track save status for each question
    const [saveStatuses, setSaveStatuses] = useState<
        Record<number, SaveStatus>
    >({});
    const savedTimeouts = useRef<Record<number, NodeJS.Timeout>>({});

    // Update save status for a question
    const updateSaveStatus = (questionId: number, status: SaveStatus) => {
        setSaveStatuses((prev) => ({ ...prev, [questionId]: status }));

        // If status is "saved", set a timeout to hide the indicator after 3 seconds
        if (status === "saved") {
            // Clear any existing timeout for this question
            if (savedTimeouts.current[questionId]) {
                clearTimeout(savedTimeouts.current[questionId]);
            }

            // Set new timeout to change status to idle
            savedTimeouts.current[questionId] = setTimeout(() => {
                setSaveStatuses((prev) => ({ ...prev, [questionId]: "idle" }));
                delete savedTimeouts.current[questionId];
            }, 3000);
        }
    };

    // Cleanup timeouts on unmount
    useEffect(() => {
        const timeouts = savedTimeouts.current;
        return () => {
            Object.values(timeouts).forEach(clearTimeout);
        };
    }, []);

    // Debounce only the server save, not the local state update
    const debouncedSaveToServer = React.useMemo(
        () =>
            debounce(async (questionId: number, answerText: string) => {
                updateSaveStatus(questionId, "saving");
                try {
                    const saveRequest = new SaveQuestionRequest(
                        AuthContextHolder.getAuthContext(),
                        questionId,
                        answerText
                    );
                    await saveRequest.makeRequest();
                    updateSaveStatus(questionId, "saved");
                } catch (error) {
                    console.error("Error saving answer:", error);
                    // On error, revert to unsaved
                    updateSaveStatus(questionId, "unsaved");
                }
            }, 2000),
        []
    );

    const handleAnswerChange = (questionId: number, answerText: string) => {
        console.log(
            `🔵 handleAnswerChange called - questionId: ${questionId}, answerText length: ${answerText.length}`
        );

        // Set status to unsaved immediately
        updateSaveStatus(questionId, "unsaved");

        // Update local state immediately
        if (answersData) {
            // Check if answer exists
            const existingAnswerIndex = answersData.data.findIndex(
                (answer) => answer.lessonDayQuestionId === questionId
            );

            console.log(`🔵 Existing answer index: ${existingAnswerIndex}`);

            let newData;
            if (existingAnswerIndex !== -1) {
                // Update existing answer
                newData = answersData.data.map((answer) => {
                    if (answer.lessonDayQuestionId === questionId) {
                        return { ...answer, answerText };
                    }
                    return answer;
                });
                console.log(`🔵 Updated existing answer`);
            } else {
                // Create new answer entry
                const newAnswer = {
                    lessonDayQuestionId: questionId,
                    answerText,
                    creationTime: new Date().toISOString(),
                    lastModifiedTime: new Date().toISOString(),
                    personId: 0, // This will be set by the server
                };
                newData = [...answersData.data, newAnswer];
                console.log(`🔵 Created new answer`);
            }

            const newAnswerData = { ...answersData, data: newData };
            console.log(
                `🔵 Calling onAnswerChange with ${newData.length} answers`
            );
            onAnswerChange(newAnswerData);
        }

        // Debounce the server save
        debouncedSaveToServer(questionId, answerText);
    };

    if (!lessonDay) {
        return <div>Loading Lesson Day Information...</div>;
    }

    const getAnswerForQuestion = (questionId: number): string | undefined => {
        const foundAnswer = answersData?.data.find(
            (answer) => answer.lessonDayQuestionId === questionId
        );
        console.log(
            `🟢 getAnswerForQuestion(${questionId}) - found: ${
                foundAnswer ? "yes" : "no"
            }, text length: ${foundAnswer?.answerText?.length || 0}`
        );
        return foundAnswer?.answerText;
    };

    const questionShouldBeVisible = (
        question: LessonDayQuestion
    ): boolean | undefined => {
        return question?.isAnswerRequired || isPassageDiscovery(question);
    };

    const isPassageDiscovery = (question: LessonDayQuestion): boolean => {
        return (
            question?.lessonDayQuestionTranslations[0].questionText.startsWith(
                "Passage Discovery"
            ) ||
            question?.lessonDayQuestionTranslations[0].questionText.startsWith(
                "Homiletics"
            )
        );
    };

    // this will build the verses context for the question, starting with the Scripture for the day,
    // then adding any additional scriptures for the question
    const extractPlaintextScriptureDataForLesson = (
        lessonDay: LessonDay,
        scripturesData: AllScripturesResponse | undefined,
        suggestionsContext: LessonDayQuestion
    ): string[] => {
        const dailyScriptures = lessonDay?.lessonDayScriptures;
        const dailyScripturesData = dailyScriptures.map((ds) =>
            scripturesData?.data.find((s) => s.scriptureId === ds.scriptureId)
        );
        const questionScriptures =
            suggestionsContext.lessonDayQuestionScriptures;
        const questionScriptureData = questionScriptures.map((qs) =>
            scripturesData?.data.find((s) => s.scriptureId === qs.scriptureId)
        );
        const allScripturesData = dailyScripturesData.concat(
            questionScriptureData
        );
        const allScriptures = dailyScriptures.concat(questionScriptures);

        return allScripturesData.map((scripture, index) => {
            const htmlVersion = scripture?.htmlContent;
            // remove the first and last character (since they're just quotes) from the htmlVersion
            const strippedHtmlVersion = htmlVersion
                ?.slice(1, -1)
                .replaceAll('\\"', '"');
            const parser = new DOMParser();
            const doc = parser.parseFromString(
                strippedHtmlVersion || "",
                "text/html"
            );

            const spansWithDataCaller =
                doc.querySelectorAll("span[data-caller]");
            spansWithDataCaller.forEach((span) => {
                span.parentNode?.removeChild(span);
            });

            const spansWithDataNumber =
                doc.querySelectorAll("span[data-number]");
            spansWithDataNumber.forEach((span) => {
                const dataNumberValue = span.getAttribute("data-number");
                const textNode = doc.createTextNode(
                    " " + (dataNumberValue || "") + ": "
                );
                span.parentNode?.replaceChild(textNode, span);
            });

            const currentScriptureReferenceData = allScriptures[index];
            return (
                scripture?.longName +
                    " " +
                    (currentScriptureReferenceData.scripture?.chapterVerses ??
                        scripture?.chapterVerses) +
                    " - " +
                    doc.body.textContent || ""
            );
        });
    };

    const buildConversationConfig = (
        scriptures: string[],
        questionText: string,
        existingAnswer: string
    ): ConversationConfig | null => {
        // if the user hasn't actually started a new sentence, we actually don't want to offer any suggestions yet
        // we can use a regex to see if it ends with a period and any number of other whitespace characters

        // TODO: look for other punctuation marks, like question marks, exclamation points, etc.

        const regex = new RegExp("\\.\\s*$");
        if (regex.test(existingAnswer)) {
            return null;
        }

        const [firstPart, secondPart]: string[] =
            StringUtils.splitOnLastIndexOf(existingAnswer, ". ");

        let scripturesPrompt = "";
        if (scriptures.length > 0) {
            scripturesPrompt =
                "Here are the verses I've read: \n" +
                scriptures.join("\n") +
                "\n\n";
        }

        let userContent =
            scripturesPrompt +
            "I am answering the following question: \n" +
            questionText +
            "\n\n";

        let existingTextLength = tokenCount(firstPart);
        if (secondPart) {
            // if there's more than one sentence, we need to add all but the last one to the content
            userContent += "So far, I've written this: \n" + firstPart + "\n\n";
            userContent += completionPhrase + "\n" + secondPart;
            existingTextLength = tokenCount(secondPart);
        } else {
            userContent += completionPhrase + "\n" + firstPart;
        }

        const config: ConversationConfig = {
            messages: [
                {
                    role: "system",
                    content:
                        "These are the specifications by which you will tailor your responses: \n" +
                        "1) You are generating sentences to aid users with Bible studies by offering the completion of a sentence they've already typed. \n" +
                        "2) You will sometimes be provided with verses the user is referencing. \n" +
                        "3) You will expect the question they're answering, and the text they've written. \n" +
                        "4) You will provide a completed version of their sentence using context from the verses, question, and what was already written. \n" +
                        '5) You will respond with the full sentence to be completed. You will recognize it by the phrase "' +
                        completionPhrase +
                        '"\n' +
                        '6) You must not return a response which is just "' +
                        completionPhrase +
                        '" or anything similar to that.',
                },
                {
                    role: "user",
                    content: userContent,
                },
            ],
            max_tokens: existingTextLength + settings.settings.typeaheadLength,
            n: 1,
            stop: ["\n", "."],
            model: "gpt-4o",
        };

        return config;
    };

    // counts the occurrences of spaces, periods, and commas in phrase to calculate token length:
    const tokenCount = (phrase: string): number => {
        const matches = phrase.match(/[\s.,]/g);
        return matches ? matches.length : 0;
    };

    const generateSuggestions = async (
        input: string,
        suggestionsContext: LessonDayQuestion
    ): Promise<Array<string>> => {
        if (settings.settings.typeaheadSuggestions === false) {
            // if typeahead suggestions are disabled, return an empty array
            return [];
        }
        if (settings.settings.typeaheadApiKey === "") {
            console.warn(
                "OpenAI API Key is not set, typeahead suggestions will not work"
            );
            return [];
        }

        console.log("generateSuggestions called with input: " + input);

        const plaintextScriptures = extractPlaintextScriptureDataForLesson(
            lessonDay,
            scripturesData,
            suggestionsContext
        );
        // find the actual question text being asked for the question
        const questionText =
            suggestionsContext.lessonDayQuestionTranslations[0].questionText;

        //let's build the conversation config:
        const conversationConfig = buildConversationConfig(
            plaintextScriptures,
            questionText,
            input
        );
        if (!conversationConfig) {
            return [];
        }

        console.log("*********************");
        console.log(
            "conversationConfig: " + JSON.stringify(conversationConfig)
        );
        const openAiRequest = new OpenAiCompletionRequest();
        const openAiResponse = await openAiRequest.makeRequest(
            conversationConfig,
            settings.settings.typeaheadApiKey
        );
        console.log("openAiResponse: " + JSON.stringify(openAiResponse));
        console.log("*********************");

        // iterate through the choices  and return the choice -> message -> content
        return openAiResponse.choices.map((choice) => {
            return processMessageContent(choice.message.content);
        });
    };

    const processMessageContent = (content: string): string => {
        // Sometimes the message starts with "The sentence I want completed starts with this:"
        // Let's remove that

        return content.replace(completionPhrase, "").trimStart();
    };

    const marginRight = pinnedScriptures.length > 0 ? panelWidth : 0;

    return (
        <div
            className="lesson-area-container"
            style={{ marginRight: `${marginRight}px` }}
        >
            <div className="lesson-area">
                {/* Display Lesson Day Title */}
                <h2>{lessonDay.lessonDayTranslations[0].mainTitle}</h2>
                <p>{lessonDay.lessonDayTranslations[0].subTitle}</p>
                {/* Display Scriptures of the Day, but only if it has any */}
                {lessonDay.lessonDayScriptures.length > 0 && (
                    <div className="scriptures">
                        <h3>Scriptures for the Day:</h3>
                        <ul>
                            {lessonDay.lessonDayScriptures.map(
                                (scriptureReference) => {
                                    const matchingScripture =
                                        scripturesData?.data.find(
                                            (s) =>
                                                s.scriptureId ===
                                                scriptureReference.scriptureId
                                        );
                                    return (
                                        <li
                                            key={scriptureReference.scriptureId}
                                        >
                                            {matchingScripture && (
                                                <Scripture
                                                    scriptureData={
                                                        matchingScripture
                                                    }
                                                    verseReferences={
                                                        scriptureReference.scripture?.chapterVerses ??
                                                        matchingScripture.chapterVerses ??
                                                        ""
                                                    }
                                                    hideWhenPinned={true}
                                                />
                                            )}
                                        </li>
                                    );
                                }
                            )}
                        </ul>
                    </div>
                )}
                {/* Display Lecture and Notes, if any, but only on the first day, and show it for previousLessonDay (if defined)*/}
                {lessonDay.dayOfWeek === 1 &&
                    previousLessonId !== undefined && (
                        <LectureAndNotesComponent lessonId={previousLessonId} />
                    )}
                {/* Display Questions */}
                <div className="questions">
                    {lessonDay.lessonDayQuestions.map((question) => (
                        <div
                            key={question.lessonDayQuestionId}
                            className="question"
                        >
                            <h4>
                                {question.questionNumber}
                                {question.questionSubNumber &&
                                    `.${question.questionSubNumber}`}
                                :&nbsp;
                                {
                                    question.lessonDayQuestionTranslations[0]
                                        .questionText
                                }
                            </h4>
                            {/* List scriptures for the question, if any */}
                            {question.lessonDayQuestionScriptures.length >
                                0 && (
                                <ul>
                                    {question.lessonDayQuestionScriptures.map(
                                        (qScripture) => {
                                            const matchingScripture =
                                                scripturesData?.data.find(
                                                    (s) =>
                                                        s.scriptureId ===
                                                        qScripture.scriptureId
                                                );
                                            return (
                                                <li
                                                    key={qScripture.scriptureId}
                                                >
                                                    {matchingScripture && (
                                                        <Scripture
                                                            scriptureData={
                                                                matchingScripture
                                                            }
                                                            verseReferences={
                                                                qScripture.scripture?.chapterVerses ??
                                                                matchingScripture.chapterVerses ??
                                                                ""
                                                            }
                                                            hideWhenPinned={
                                                                true
                                                            }
                                                        />
                                                    )}
                                                </li>
                                            );
                                        }
                                    )}
                                </ul>
                            )}
                            {/* Provide Input area to answer the question */}
                            {questionShouldBeVisible(question) ? (
                                <div className="answer-input-container">
                                    <TypeaheadTextarea
                                        key={question.lessonDayQuestionId}
                                        generateSuggestions={
                                            generateSuggestions
                                        }
                                        suggestionsContext={question}
                                        suggestionsDebounceTime={1500}
                                        rows={4}
                                        additionalClassNames={
                                            isPassageDiscovery(question)
                                                ? "passage-discovery-question"
                                                : "standard-question"
                                        }
                                        placeholder="Write your answer here..."
                                        defaultValue={getAnswerForQuestion(
                                            question.lessonDayQuestionId
                                        )}
                                        onChange={(text) =>
                                            handleAnswerChange(
                                                question.lessonDayQuestionId,
                                                text
                                            )
                                        }
                                    />
                                    <SaveIndicator
                                        status={
                                            saveStatuses[
                                                question.lessonDayQuestionId
                                            ] || "idle"
                                        }
                                    />
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>
            <PinnedScripturesPanel />
        </div>
    );
};

const LessonAreaComponent: React.FC<LessonDayProps> = (props) => {
    return (
        <PinnedScripturesProvider>
            <LessonAreaContent {...props} />
        </PinnedScripturesProvider>
    );
};

export default LessonAreaComponent;
